import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Button,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import {
  createActivitySagaAction,
  createPhaseSagaAction,
  deleteActivitySagaAction,
  deletePhaseSagaAction,
  getPhasesSagaAction,
  reorderPhasesSagaAction,
  updateActivitySagaAction,
  updatePhaseSagaAction,
} from "../../../../../../redux/saga/technology-settings/phases/saga-actions";
import { technologySettingsPhasesSliceAction } from "../../../../../../redux/slices/technology-settings/phases";
import type { ActivityItem, PhaseItem } from "../types";
import ViewDescriptionModal from "../components/ViewDescriptionModal";
import RichTextPreview from "../components/RichTextPreview";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import AddPhaseModal, { PhaseFormData } from "./AddPhaseModal";
import AddActivityModal, { ActivityFormData } from "./AddActivityModal";

const IntegrationPhases: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const phases = useAppSelector((state) => state.technology_settings_phases.phases);
  const isPhasesLoading = useAppSelector(
    (state) => state.technology_settings_phases.status.phasesLoading
  );
  const isCreatePhaseLoading = useAppSelector(
    (state) => state.technology_settings_phases.status.createPhaseLoading
  );
  const isUpdatePhaseLoading = useAppSelector(
    (state) => state.technology_settings_phases.status.updatePhaseLoading
  );
  const isPhaseSubmitting = isCreatePhaseLoading || isUpdatePhaseLoading;
  const isCreateActivityLoading = useAppSelector(
    (state) => state.technology_settings_phases.status.createActivityLoading
  );
  const isUpdateActivityLoading = useAppSelector(
    (state) => state.technology_settings_phases.status.updateActivityLoading
  );
  const isActivitySubmitting =
    isCreateActivityLoading || isUpdateActivityLoading;
  const isDeletePhaseLoading = useAppSelector(
    (state) => state.technology_settings_phases.status.deletePhaseLoading
  );
  const isDeleteActivityLoading = useAppSelector(
    (state) => state.technology_settings_phases.status.deleteActivityLoading
  );
  const isDeleteSubmitting = isDeletePhaseLoading || isDeleteActivityLoading;
  const [toasts, setToasts] = useState<any[]>([]);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [isPhaseModalOpen, setIsPhaseModalOpen] = useState(false);
  const [phaseModalMode, setPhaseModalMode] = useState<"add" | "edit">("add");
  const [editingPhase, setEditingPhase] = useState<PhaseItem | null>(null);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [activityModalMode, setActivityModalMode] = useState<"add" | "edit">("add");
  const [activityPhaseId, setActivityPhaseId] = useState<string | null>(null);
  const [editingActivity, setEditingActivity] = useState<ActivityItem | null>(null);
  const [descriptionView, setDescriptionView] = useState<{
    title: string;
    content: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "phase" | "activity";
    phaseId: string;
    activityId?: string;
    name: string;
  } | null>(null);
  const [draggedPhaseIndex, setDraggedPhaseIndex] = useState<number | null>(null);

  useEffect(() => {
    dispatch(getPhasesSagaAction());
  }, [dispatch]);

  const pushToast = (variant: "success" | "error", message: string) => {
    setToasts((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: "",
        message,
        variant:
          variant === "success"
            ? NotificationVariants.Success
            : NotificationVariants.Error,
        icon: (
          <Iconography
            icon={variant === "success" ? "circleCheck" : "ban"}
            size={IconographySizes.Medium}
          />
        ),
      },
    ]);
  };

  const closePhaseModal = () => {
    setIsPhaseModalOpen(false);
    setEditingPhase(null);
  };

  const closeActivityModal = () => {
    setIsActivityModalOpen(false);
    setActivityPhaseId(null);
    setEditingActivity(null);
  };

  const toggleRow = (phaseId: string) => {
    setExpandedRows((prev) =>
      prev.includes(phaseId)
        ? prev.filter((id) => id !== phaseId)
        : [...prev, phaseId]
    );
  };

  const handleAddPhase = (data: PhaseFormData) => {
    dispatch(
      createPhaseSagaAction({
        data: {
          phase_name: data.name.trim(),
          description: data.description,
        },
        onSuccess: (message) => {
          pushToast("success", message || "Phase added successfully.");
          closePhaseModal();
        },
        onError: (message) => {
          pushToast("error", message || "Unable to create phase.");
        },
      })
    );
  };

  const handleEditPhase = (data: PhaseFormData) => {
    if (!editingPhase) return;
    dispatch(
      updatePhaseSagaAction({
        id: editingPhase.id,
        data: {
          phase_name: data.name.trim(),
          description: data.description,
        },
        onSuccess: (message) => {
          pushToast("success", message || "Phase updated successfully.");
          closePhaseModal();
        },
        onError: (message) => {
          pushToast("error", message || "Unable to update phase.");
        },
      })
    );
  };

  const handleAddActivity = (data: ActivityFormData) => {
    if (!activityPhaseId) return;
    dispatch(
      createActivitySagaAction({
        phaseId: activityPhaseId,
        data: {
          activity_name: data.name.trim(),
          description: data.description,
        },
        onSuccess: (message) => {
          pushToast("success", message || "Activity added successfully.");
          closeActivityModal();
        },
        onError: (message) => {
          pushToast("error", message || "Unable to create activity.");
        },
      })
    );
  };

  const handleEditActivity = (data: ActivityFormData) => {
    if (!editingActivity) return;
    dispatch(
      updateActivitySagaAction({
        id: editingActivity.id,
        data: {
          activity_name: data.name.trim(),
          description: data.description,
        },
        onSuccess: (message) => {
          pushToast("success", message || "Activity updated successfully.");
          closeActivityModal();
        },
        onError: (message) => {
          pushToast("error", message || "Unable to update activity.");
        },
      })
    );
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget || isDeleteSubmitting) return;
    const target = deleteTarget;

    if (target.type === "phase") {
      dispatch(
        deletePhaseSagaAction({
          id: target.phaseId,
          onSuccess: (message) => {
            pushToast("success", message || "Phase deleted successfully.");
            setExpandedRows((prev) => prev.filter((id) => id !== target.phaseId));
            setDeleteTarget(null);
          },
          onError: (message) => {
            pushToast("error", message || "Unable to delete phase.");
          },
        })
      );
      return;
    }

    if (target.activityId) {
      dispatch(
        deleteActivitySagaAction({
          id: target.activityId,
          onSuccess: (message) => {
            pushToast("success", message || "Activity deleted successfully.");
            setDeleteTarget(null);
          },
          onError: (message) => {
            pushToast("error", message || "Unable to delete activity.");
          },
        })
      );
    }
  };

  const handlePhaseDragStart = (
    event: React.DragEvent<HTMLSpanElement>,
    index: number
  ) => {
    if (readOnly) return;
    setDraggedPhaseIndex(index);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
  };

  const handlePhaseDrop = (
    event: React.DragEvent<HTMLTableRowElement>,
    targetIndex: number
  ) => {
    event.preventDefault();
    if (readOnly) return;
    const sourceFromDataTransfer = Number(event.dataTransfer.getData("text/plain"));
    const sourceIndex =
      draggedPhaseIndex !== null
        ? draggedPhaseIndex
        : Number.isNaN(sourceFromDataTransfer)
          ? null
          : sourceFromDataTransfer;

    if (sourceIndex === null || sourceIndex === targetIndex) {
      setDraggedPhaseIndex(null);
      return;
    }

    const next = [...phases];
    if (
      sourceIndex < 0 ||
      targetIndex < 0 ||
      sourceIndex >= next.length ||
      targetIndex >= next.length
    ) {
      setDraggedPhaseIndex(null);
      return;
    }

    const [movedPhase] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, movedPhase);
    const reorderedPhases = next.map((phase, index) => ({
      ...phase,
      order: index + 1,
    }));

    dispatch(technologySettingsPhasesSliceAction.setPhases(reorderedPhases));
    dispatch(
      reorderPhasesSagaAction({
        data: {
          phases: reorderedPhases.map((phase) => ({
            ID: Number(phase.id),
          })),
        },
        onError: (message) => {
          pushToast("error", message || "Unable to reorder phases.");
        },
      })
    );
    setDraggedPhaseIndex(null);
  };

  return (
    <div className="technology-management-content feature-management-content">
      <Notification toasts={toasts} setToasts={setToasts} />
      {isPhasesLoading ? (
        <div className="feature-loader-wrap">
          <Loader variant={LoaderSizes.large} />
        </div>
      ) : (
      <div className="features-section">
        <div className="fm-top-row tm-section-header">
          <div>
            <Typography variant="h6" className="section-title">
              Integration Phases
            </Typography>
            <Typography className="section-subtitle">
              Configure Integration Phases
            </Typography>
          </div>
          <div className="fm-actions">
            <Button
              variant="primary"
              label="ADD PHASE"
              icon={<Iconography icon="plus" />}
              disabled={readOnly}
              onClick={() => {
                if (readOnly) return;
                setPhaseModalMode("add");
                setEditingPhase(null);
                setIsPhaseModalOpen(true);
              }}
            />
          </div>
        </div>

        <TableContainer component={Paper} className="features-table">
          <Table className="features-main-table tm-phases-table">
            <colgroup>
              <col className="col-feature-name" />
              <col className="col-description" />
              <col className="col-usp" />
              <col className="col-actions" />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell className="feature-col-feature tm-phase-col">
                  Phase Name
                </TableCell>
                <TableCell className="feature-col-desc">Description</TableCell>
                <TableCell className="feature-col-usp">No. of Activities</TableCell>
                <TableCell className="feature-actions-col">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {phases.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="feature-empty-cell">
                    No integration phases found.
                  </TableCell>
                </TableRow>
              )}
              {phases.map((phase, index) => {
                const isExpanded = expandedRows.includes(phase.id);
                const activityLabel =
                  phase.activities.length === 1
                    ? "1 activity"
                    : `${phase.activities.length} activities`;
                const isDragging = draggedPhaseIndex === index;

                return (
                  <React.Fragment key={phase.id}>
                    <TableRow
                      className={isDragging ? "tm-row-dragging" : undefined}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => handlePhaseDrop(event, index)}
                    >
                      <TableCell className="feature-col-feature tm-phase-col">
                        <div className="feature-name-cell tm-phase-name-cell">
                          <span
                            className={`tm-drag-handle drag-handle${
                              readOnly ? " tm-drag-handle--disabled" : ""
                            }`}
                            draggable={!readOnly}
                            onDragStart={(event) =>
                              handlePhaseDragStart(event, index)
                            }
                            onDragEnd={() => setDraggedPhaseIndex(null)}
                            title={readOnly ? undefined : "Drag to reorder"}
                            aria-label={readOnly ? undefined : "Drag to reorder"}
                            role="button"
                          >
                            <span className="drag-dots-icon" aria-hidden="true" />
                          </span>
                          <span className="tm-phase-badge">{phase.order}</span>
                          <span>{phase.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="feature-col-desc">
                        <button
                          type="button"
                          className="view-link"
                          onClick={() =>
                            setDescriptionView({
                              title: phase.name,
                              content: phase.description,
                            })
                          }
                        >
                          View
                        </button>
                      </TableCell>
                      <TableCell className="feature-col-usp">{activityLabel}</TableCell>
                      <TableCell className="feature-actions-col">
                        <div className="feature-actions-cell">
                          <Button
                            variant="flat"
                            icon={<Iconography icon="pen" />}
                            disabled={readOnly}
                            onClick={() => {
                              if (readOnly) return;
                              setPhaseModalMode("edit");
                              setEditingPhase(phase);
                              setIsPhaseModalOpen(true);
                            }}
                          />
                          <Button
                            variant="flat"
                            icon={<Iconography icon="trashCan" />}
                            disabled
                          />
                          <Button
                            variant="flat"
                            className="feature-expand-btn"
                            onClick={() => toggleRow(phase.id)}
                            icon={
                              <Iconography
                                icon={isExpanded ? "angleUp" : "angleDown"}
                                size={IconographySizes.Small}
                              />
                            }
                            aria-label={`${isExpanded ? "Collapse" : "Expand"} ${phase.name}`}
                          />
                        </div>
                      </TableCell>
                    </TableRow>

                    {isExpanded && (
                      <TableRow className="expanded-details-row">
                        <TableCell colSpan={4} className="expanded-details-cell">
                          <div className="feature-details-card">
                            <div className="tm-details-card-header">
                              <Typography className="tm-details-card-header-title">
                                Activities
                              </Typography>
                              <Button
                                variant="flat"
                                className="tm-details-card-header-action"
                                label="+ Add New Activity"
                                disabled={readOnly}
                                onClick={() => {
                                  if (readOnly) return;
                                  setActivityModalMode("add");
                                  setActivityPhaseId(phase.id);
                                  setEditingActivity(null);
                                  setIsActivityModalOpen(true);
                                }}
                              />
                            </div>
                            <Table
                              size="small"
                              className="feature-details-table tm-activity-details-table"
                            >
                              <colgroup>
                                <col className="detail-col-1" />
                                <col className="detail-col-2" />
                                <col className="detail-col-3" />
                              </colgroup>
                              <TableHead>
                                <TableRow>
                                  <TableCell className="detail-th-tech">
                                    Activity Name
                                  </TableCell>
                                  <TableCell className="detail-th-req">
                                    Description
                                  </TableCell>
                                  <TableCell className="detail-th-actions">
                                    Actions
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {phase.activities.length === 0 && (
                                  <TableRow>
                                    <TableCell colSpan={3} className="feature-empty-cell">
                                      No activities added yet.
                                    </TableCell>
                                  </TableRow>
                                )}
                                {phase.activities.map((activity) => (
                                  <TableRow key={activity.id}>
                                    <TableCell className="detail-td-tech">
                                      {activity.name}
                                    </TableCell>
                                    <TableCell className="detail-td-req">
                                      <RichTextPreview content={activity.description} />
                                    </TableCell>
                                    <TableCell className="detail-td-actions">
                                      <div className="feature-actions-cell">
                                        <Button
                                          variant="flat"
                                          icon={<Iconography icon="pen" />}
                                          disabled={readOnly}
                                          onClick={() => {
                                            if (readOnly) return;
                                            setActivityModalMode("edit");
                                            setActivityPhaseId(phase.id);
                                            setEditingActivity(activity);
                                            setIsActivityModalOpen(true);
                                          }}
                                        />
                                        <Button
                                          variant="flat"
                                          icon={<Iconography icon="trashCan" />}
                                          disabled
                                        />
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      )}

      <AddPhaseModal
        open={isPhaseModalOpen}
        mode={phaseModalMode}
        isSubmitting={isPhaseSubmitting}
        initialData={
          editingPhase
            ? {
                name: editingPhase.name,
                description: editingPhase.description,
              }
            : null
        }
        onClose={closePhaseModal}
        onSubmit={phaseModalMode === "add" ? handleAddPhase : handleEditPhase}
      />

      <AddActivityModal
        open={isActivityModalOpen}
        mode={activityModalMode}
        isSubmitting={isActivitySubmitting}
        phaseName={
          phases.find((phase) => phase.id === activityPhaseId)?.name || "Phase"
        }
        initialData={
          editingActivity
            ? {
                name: editingActivity.name,
                description: editingActivity.description,
              }
            : null
        }
        onClose={closeActivityModal}
        onSubmit={
          activityModalMode === "add" ? handleAddActivity : handleEditActivity
        }
      />

      <ViewDescriptionModal
        open={Boolean(descriptionView)}
        eyebrow="Technology Management"
        title="View Description"
        subtitle={descriptionView?.title || ""}
        content={descriptionView?.content || ""}
        onClose={() => setDescriptionView(null)}
      />

      <DeleteConfirmModal
        open={Boolean(deleteTarget)}
        title={deleteTarget?.type === "phase" ? "Delete Phase?" : "Delete Activity?"}
        itemName={deleteTarget?.name || ""}
        message="Are you sure you want to delete"
        isSubmitting={isDeleteSubmitting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default IntegrationPhases;
